import { getValidPrice } from "../helpers/getValidPrice";

export type StockStatus =
  | "in-stock"
  | "low-stock"
  | "backorder"
  | "out-of-stock"
  | "unknown"
  | "unavailable";

export const STOCK_STATUS_CONFIG: Record<
  StockStatus,
  { label: string; dotColor: string }
> = {
  "in-stock": { label: "In Stock", dotColor: "success.main" },
  "low-stock": { label: "Low Stock", dotColor: "warning.dark" },
  backorder: { label: "Backorder", dotColor: "error.main" },
  "out-of-stock": { label: "Out of Stock", dotColor: "error.dark" },
  unknown: { label: "Verify Stock", dotColor: "grey.700" },
  unavailable: { label: "Unavailable", dotColor: "grey.700" },
};

export const priceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

export type PriceFormatter = (price: number) => string;

const formatUsd: PriceFormatter = (price) => priceFormatter.format(price);

export function getAccessiblePriceLabel(
  price: number | null | undefined,
  formatPrice: PriceFormatter = formatUsd,
): string {
  const validPrice = getValidPrice(price);

  if (price == null) {
    return "Price unavailable";
  }

  if (validPrice == null) {
    return "Call for price";
  }

  return `Price: ${formatPrice(validPrice)}`;
}

export function getVisiblePriceLabel(
  price: number | null | undefined,
  formatPrice: PriceFormatter = formatUsd,
): string {
  const validPrice = getValidPrice(price);

  if (price == null) {
    return "—";
  }

  if (validPrice == null) {
    return "Call for Price";
  }

  return formatPrice(validPrice);
}

export function getProductDisplayName(product: {
  name?: string | null;
  manufacturer?: string | null;
}): string {
  const name = product.name ?? "";
  const manufacturer = product.manufacturer;

  return manufacturer && name.startsWith(`${manufacturer} `)
    ? name.slice(manufacturer.length + 1)
    : name;
}

export function mapInventoryStatusToStockStatus(
  inventoryStatus?: string | null,
): StockStatus {
  // InventoryStatusCalculator returns null for every non-commodity product (licensing,
  // software downloads, config items), so an absent status means "not applicable", not
  // "out of stock".
  if (!inventoryStatus) return "unknown";
  const s = inventoryStatus.toLowerCase().trim();
  if (s.startsWith("in stock") || s === "available") return "in-stock";
  if (s.includes("low") || s.includes("limited")) return "low-stock";
  if (s.includes("backorder") || s.includes("back order")) return "backorder";
  if (s.includes("unavailable")) return "unavailable";
  // InventoryStatusCalculator emits "Verify Stock" and "Unknown Stock Status" when the stock
  // level could not be determined. Those are not a zero-quantity result, so they must not
  // render as a red "Out of Stock".
  if (s.includes("verify") || s.includes("unknown")) return "unknown";
  return "out-of-stock";
}

// Pixel strings: in MUI sx a bare `1` means 100% and `-1` is a theme spacing unit.
export const srOnlySx = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
} as const;
